/**
 * Shared shape for a review card. Metadata is one half, stacked one field
 * per line at a fixed height so a long name does not move the controls.
 * The other half is the message or the photo. Green is shared, red is
 * don't-share, gray is not shared.
 */
import { type CSSProperties, type ReactNode } from "react"

import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import {
  deriveFamilyState,
  parentShareText,
} from "#utils/standoutMomentMigration"

/** Mid-dark gray. About 7:1 on white, and above AA on the tinted card. */
export const REVIEW_MUTED = "#555555"

/** Earlier-comment column on the right of a kid's week card. */
export const COMMENT_HISTORY_STRIP_PX = 400

/** Yellow for every note in a similar set: the card item and the earlier comment. */
export const COMMENT_HISTORY_SIMILAR = "#ffe56a"

export const REVIEW_BLOCK = 216
export const REVIEW_LINE = 22
export const REVIEW_SCORE_SLOT = 72

export type ReviewEdge = "shared" | "blocked" | "unshared"

export const reviewEdge = (blocked: boolean, shared: boolean): ReviewEdge => {
  if (blocked) return "blocked"
  if (shared) return "shared"
  return "unshared"
}

export const reviewEdgeColor = (
  edge: ReviewEdge,
  colors: { green: string; red: string; gray: string }
): string => {
  if (edge === "shared") return colors.green
  if (edge === "blocked") return colors.red
  return colors.gray
}

export const reviewLineStyle: CSSProperties = {
  height: REVIEW_LINE,
  minHeight: REVIEW_LINE,
  maxHeight: REVIEW_LINE,
  lineHeight: `${REVIEW_LINE}px`,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  minWidth: 0,
}

export const reviewStatusStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
  width: "100%",
  minWidth: 0,
}

export const reviewHalfStyle: CSSProperties = {
  flex: "1 1 0",
  width: "50%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
}

const sentListStyle: CSSProperties = {
  width: "100%",
  maxHeight: 180,
  overflowY: "auto",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  minWidth: 0,
  marginTop: 8,
}

export type SentFamilyNote = {
  id: string
  studentId: string
  at: number
  dateLabel: string
  className: string
  text: string
}

const normalizeSentText = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, " ")

/** The card text and a previously sent sentence are the same message. */
export const sentTextOverlaps = (
  cardText: string,
  sentText: string
): boolean => {
  const card = normalizeSentText(cardText)
  const sent = normalizeSentText(sentText)
  if (!card || !sent) return false
  if (card === sent) return true
  const shorter = Math.min(card.length, sent.length)
  if (shorter < 12) return false
  return card.includes(sent) || sent.includes(card)
}

/**
 * Messages already sent to the family, newest first, grouped by student.
 * Secret and don't-share are not family sends. Story Time and artwork are
 * not in this list.
 */
export const buildSentFamilyNotes = (
  moments: readonly Pick<
    StandoutMomentConstructor,
    | "id"
    | "studentId"
    | "classId"
    | "category"
    | "dontShare"
    | "familyState"
    | "isShared"
    | "note"
    | "generatedNote"
    | "familyNoteSource"
    | "createdAt"
  >[],
  classNameFor: (classId: string | undefined) => string,
  dateLabelFor: (createdAt: string | undefined) => { at: number; label: string }
): SentFamilyNote[] => {
  const notes: SentFamilyNote[] = []
  for (const moment of moments) {
    if (moment.category === "secret" || moment.dontShare === true) continue
    if (deriveFamilyState(moment) !== "sent") continue
    const text = parentShareText(moment).trim()
    if (!text) continue
    const dated = dateLabelFor(moment.createdAt)
    notes.push({
      id: moment.id,
      studentId: moment.studentId,
      at: dated.at,
      dateLabel: dated.label,
      className: classNameFor(moment.classId) || "Class",
      text,
    })
  }
  notes.sort((a, b) => b.at - a.at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  return notes
}

export const ReviewField = ({ children }: { children: ReactNode }) => (
  <div style={reviewLineStyle}>{children}</div>
)

export const SentFamilyNoteList = ({
  notes,
  cardText,
  cardTexts,
  markColor,
}: {
  notes: readonly SentFamilyNote[]
  cardText?: string
  cardTexts?: readonly string[]
  markColor: string
}) => (
  <div data-testid="sr-sent-notes" style={sentListStyle}>
    {notes.length === 0 ? (
      <span style={{ ...reviewLineStyle, color: REVIEW_MUTED }}>None sent</span>
    ) : (
      notes.map((note) => {
        const texts = cardTexts ?? (cardText ? [cardText] : [])
        const overlap = texts.some((text) => sentTextOverlaps(text, note.text))
        return (
          <div
            key={note.id}
            data-testid="sr-sent-note"
            data-moment-id={note.id}
            data-overlap={overlap ? "true" : "false"}
            style={{
              flex: "0 0 auto",
              padding: "4px 6px",
              borderRadius: 4,
              background: overlap ? `${markColor}22` : "transparent",
              borderLeft: overlap
                ? `3px solid ${markColor}`
                : "3px solid transparent",
              minWidth: 0,
            }}
          >
            <div style={{ ...reviewLineStyle, fontSize: 11 }}>
              {note.dateLabel} · {note.className}
            </div>
            <div
              style={{
                fontSize: 12,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
              }}
            >
              {note.text}
            </div>
            {overlap ? (
              <div
                style={{ ...reviewLineStyle, color: markColor, fontSize: 11 }}
              >
                Already sent
              </div>
            ) : null}
          </div>
        )
      })
    )}
  </div>
)
